// Owned by the workbench: AIOE mirrors these shapes (mju-aioe/packages/shared/src/schemas/routineTriggers.ts). The workbench never mirrors it back. Keep the two identical.
/**
 * Routines that start on an event (D378). The idea of event sources beside a clock is AgentField's (`internal/sources`,
 * Agent-Field/agentfield, Apache-2.0; ideas only, no code), as the audit docs/research/2026-10-agentfield-swe-af-audit.md
 * section 6 sets out.
 *
 * A routine (shared/protocol/shell.ts `Routine`) keeps its timetable and gains `triggers`: things that happen which start
 * it as well. Four of them this machine watches itself, because the workbench host already does:
 *
 *   pipeline-failed   a pipeline run failed, on a branch or any branch (src/pipelines, the pipelines monitor)
 *   label-added       an issue or work item on the board's tracker gained a label (src/tasks, the mirrors, D238, D348)
 *   pull-request      a pull request asked for a review, or got "changes requested" (src/pullRequests, the monitor)
 *   node-alert        the node raised an alert about itself (a repository's workbench stopped unexpectedly)
 *
 * and one the organisation sends:
 *
 *   outside           the organisation's webhook intake matched a signed webhook (GitHub, Azure DevOps, Linear, Sentry,
 *                     a generic HMAC one) to a published routine, and sent this machine a device order. A routine runs
 *                     for it only when it has this trigger: a routine exists here and allows outside triggers.
 *
 * A Free workbench, or a Teams one without the organisation, keeps the four polled triggers: nothing sends the fifth.
 *
 * The device order (AIOE → the machine, on the heartbeat's `orders`, shared/schemas/deviceOrders.ts; the relay's `fleet`
 * change hint asks for that heartbeat at once):
 *
 *   { id, kind: "routine.trigger", issuedAt?, by?, expiresAt?, body: {@link RoutineTriggerOrderBody} }
 *     body.routineId   the published routine's id (the catalogue's, D363), or a routine's own id on this machine
 *     body.projectId   optional: only routines of that project (`prj_…`)
 *     body.event       {@link RoutineEvent}: source, kind, summary, url?, at
 *   answered  done      "started Nightly triage for TypeError in checkout" (one sentence per routine started)
 *             refused   with `code`: `no_routine` (none here, or none that allows outside triggers), `not_allowed` (the
 *                       policy's routine clause), `rate_limited` (the routine's limit per hour)
 *
 * Every triggered run says what started it (`RoutineRun.trigger: "event"` and `RoutineRun.event`), is limited per routine
 * ({@link triggerRefusal}: the routine's `triggerLimit`, under the policy's `routines.triggers.maxRunsPerHour`), and obeys
 * the policy's routine clause (`routines.triggers.events`: any, polled or off).
 *
 * Words for people: "When something happens", "a pipeline fails", "is labelled", "asks for review", "changes requested",
 * "the node raises an alert", "your organisation forwards an event". Never "trigger", "webhook" or "event source" on screen.
 *
 * Pure: zod and string helpers only.
 */
import { z } from "zod";
// ── Triggers ───────────────────────────────────────────────────────────
/** The kinds of trigger, in the order the editor offers them. */
export const ROUTINE_TRIGGER_KINDS = ["pipeline-failed", "label-added", "pull-request", "node-alert", "outside"];
/** What a node raises an alert about. */
export const NODE_ALERT_KINDS = ["host-stopped"];
/** Where an event from outside comes from: the services whose signed webhooks the organisation reads. */
export const OUTSIDE_EVENT_SOURCES = ["github", "azure-devops", "linear", "sentry", "webhook"];
export const OUTSIDE_SOURCE_WORDS = { github: "GitHub", "azure-devops": "Azure DevOps", linear: "Linear", sentry: "Sentry", webhook: "a webhook" };
const branchPattern = z.string().trim().min(1).max(200);
export const routineTriggerSchema = z.discriminatedUnion("kind", [
    /** A pipeline run failed on a branch: `branch` exact or with `*` ("release/*"); absent, any branch. */
    z.object({ kind: z.literal("pipeline-failed"), branch: branchPattern.optional() }),
    /** An issue or work item gained this label (or tag), whatever its case. */
    z.object({ kind: z.literal("label-added"), label: z.string().trim().min(1, "a label trigger needs the label's name").max(100) }),
    /** A pull request asked for a review, or got "changes requested". */
    z.object({ kind: z.literal("pull-request"), on: z.enum(["review-requested", "changes-requested"]) }),
    /** The node raised an alert: this one, or any. */
    z.object({ kind: z.literal("node-alert"), alert: z.enum(NODE_ALERT_KINDS).optional() }),
    /** The organisation forwards an event from outside: from this service, or any. */
    z.object({ kind: z.literal("outside"), source: z.enum(OUTSIDE_EVENT_SOURCES).optional() }),
]);
/** At most this many triggers on one routine. */
export const MAX_ROUTINE_TRIGGERS = 10;
/** A routine started by events at most this many times an hour, unless it says otherwise. */
export const DEFAULT_TRIGGER_RUNS_PER_HOUR = 3;
/** The most a routine may ask for. */
export const MAX_TRIGGER_RUNS_PER_HOUR = 12;
/** A routine's triggers as saved: checked, trimmed, each one once. A sentence when one is not a trigger. */
export function parseRoutineTriggers(raw) {
    if (raw === undefined || raw === null)
        return { triggers: [] };
    if (!Array.isArray(raw))
        return { error: "what starts a routine when something happens must be a list" };
    if (raw.length > MAX_ROUTINE_TRIGGERS)
        return { error: `a routine starts on at most ${MAX_ROUTINE_TRIGGERS} kinds of thing happening` };
    const out = [];
    const seen = new Set();
    for (const item of raw) {
        const parsed = routineTriggerSchema.safeParse(item);
        if (!parsed.success) {
            const kind = item && typeof item === "object" ? item.kind : undefined;
            const issue = parsed.error.issues[0];
            if (typeof kind !== "string" || !ROUTINE_TRIGGER_KINDS.includes(kind))
                return { error: `"${String(kind)}" is not one of the things that can start a routine when something happens` };
            return { error: issue?.message && !/^Invalid|^Too|^Expected/.test(issue.message) ? issue.message : `the ${kind} trigger is not complete: ${issue?.path.join(".") || kind}` };
        }
        const key = JSON.stringify(parsed.data);
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(parsed.data);
    }
    return { triggers: out };
}
export function triggerSources(triggers) {
    const out = new Set();
    for (const t of triggers) {
        if (t.kind === "pipeline-failed")
            out.add("pipeline");
        else if (t.kind === "label-added")
            out.add("tracker");
        else if (t.kind === "pull-request")
            out.add("pull-request");
        else if (t.kind === "node-alert")
            out.add("node");
    }
    return [...out];
}
// ── Events ─────────────────────────────────────────────────────────────
/**
 * Something that happened, as a routine's run says it: where it came from, what it was, a sentence, and where to look.
 * `source` is `pipeline`, `tracker`, `pull-request` or `node` for what this machine watched, or one of
 * {@link OUTSIDE_EVENT_SOURCES} for what the organisation forwarded. `kind` is the moment: `run-failed`, `label-added`,
 * `review-requested`, `changes-requested`, `alert`, or the forwarded webhook's own event name (`issues.labeled`).
 */
export const routineEventSchema = z.object({
    source: z.string().trim().min(1).max(40),
    kind: z.string().trim().min(1).max(80),
    /** One sentence a person reads: "CI #41 on main failed". */
    summary: z.string().trim().min(1).max(300),
    url: z.string().url().max(2000).optional(),
    /** When it happened (ISO 8601). */
    at: z.string().min(1).max(64),
    /** The branch a pipeline ran on. */
    branch: z.string().max(200).optional(),
    /** The label an item gained. */
    label: z.string().max(100).optional(),
    /** What the node raised an alert about. */
    alert: z.string().max(60).optional(),
    /** Set by the machine, never sent: the organisation forwarded it (a device order), rather than this machine watching it. */
    outside: z.boolean().optional(),
});
/** The body of the organisation's `routine.trigger` device order. */
export const routineTriggerOrderBodySchema = z.object({
    /** The published routine's id (the catalogue's, D363) or a routine's own id on this machine. */
    routineId: z.string().trim().min(1).max(200),
    /** Only routines of this project, when given. */
    projectId: z.string().max(100).optional(),
    event: routineEventSchema.omit({ outside: true }),
});
export const ROUTINE_TRIGGER_ORDER_KIND = "routine.trigger";
/** The refusal codes a `routine.trigger` order is answered with. */
export const ROUTINE_TRIGGER_REFUSALS = ["no_routine", "not_allowed", "rate_limited", "outside_not_allowed"];
const globMatches = (pattern, value) => new RegExp(`^${pattern.split("*").map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*")}$`, "i").test(value);
/** The first of a routine's triggers this event fits, or undefined. An event from outside fits only an `outside` trigger. */
export function matchingTrigger(triggers, event) {
    for (const t of triggers ?? []) {
        if (event.outside) {
            if (t.kind === "outside" && (!t.source || t.source === event.source))
                return t;
            continue;
        }
        switch (t.kind) {
            case "pipeline-failed":
                if (event.source === "pipeline" && event.kind === "run-failed" && (!t.branch || (event.branch !== undefined && globMatches(t.branch, event.branch))))
                    return t;
                break;
            case "label-added":
                if (event.source === "tracker" && event.kind === "label-added" && event.label !== undefined && event.label.toLowerCase() === t.label.toLowerCase())
                    return t;
                break;
            case "pull-request":
                if (event.source === "pull-request" && event.kind === t.on)
                    return t;
                break;
            case "node-alert":
                if (event.source === "node" && event.kind === "alert" && (!t.alert || t.alert === event.alert))
                    return t;
                break;
            case "outside":
                break;
        }
    }
    return undefined;
}
// ── Words ──────────────────────────────────────────────────────────────
/** A trigger as the editor and the routine's card say it. */
export function describeTrigger(t) {
    switch (t.kind) {
        case "pipeline-failed":
            return `A pipeline fails on ${t.branch ?? "any branch"}`;
        case "label-added":
            return `An issue or work item is labelled ${t.label}`;
        case "pull-request":
            return t.on === "review-requested" ? "A pull request asks for review" : "A pull request gets changes requested";
        case "node-alert":
            return t.alert === "host-stopped" ? "A repository's workbench on the node stops unexpectedly" : "The node raises an alert";
        case "outside":
            return t.source ? `Your organisation forwards an event from ${OUTSIDE_SOURCE_WORDS[t.source]}` : "Your organisation forwards an event";
    }
}
/** What started a run, in a sentence. */
export function describeRoutineEvent(e) {
    return e.summary;
}
/** What a run started by an event tells the agent, in front of the routine's own prompt. */
export function eventPromptPreamble(e) {
    return `This run was started because something happened: ${e.summary}${e.url ? ` (${e.url})` : ""}. Look at that first.`;
}
// ── The policy's routine clause ────────────────────────────────────────
/**
 * `routines.triggers` in the workspace policy (the routine clause, shared/schemas/routineCatalogue.ts
 * `routinesPolicySchema`). `events`: `any` (the default) lets routines start on what this machine watches and on what the
 * organisation forwards; `polled` only on what this machine watches; `off` lets them run only on their timetable or when
 * someone asks. `maxRunsPerHour`: the most any routine may be started by events in an hour, whatever it asks for.
 */
export const routineTriggerPolicySchema = z.object({
    events: z.enum(["any", "polled", "off"]).optional().describe("Whether routines may start when something happens: any (on what this machine watches and on what the organisation forwards), polled (only on what this machine watches) or off (only on their timetable or when asked). Absent: any."),
    maxRunsPerHour: z.number().int().min(1).max(60).optional().describe("The most a routine may be started by events in an hour, whatever the routine asks for. Absent: the routine's own limit (3 unless it says otherwise)."),
});
export function effectiveRoutineTriggerPolicy(p) {
    return { events: p?.events ?? "any", ...(p?.maxRunsPerHour ? { maxRunsPerHour: p.maxRunsPerHour } : {}) };
}
/** The clause in words, for the policy summary. */
export function describeRoutineTriggerPolicy(p) {
    const e = effectiveRoutineTriggerPolicy(p);
    const what = e.events === "off" ? "routines do not start on an event" : e.events === "polled" ? "routines may start on what this machine watches, not on events the organisation forwards" : "routines may start on an event";
    return e.maxRunsPerHour ? `${what}, at most ${e.maxRunsPerHour} times an hour each` : what;
}
/** How many times an hour this routine may be started by events: its own limit, under the policy's ceiling. */
export function triggerRunsPerHour(policy, own) {
    const mine = Math.min(Math.max(1, Math.floor(own ?? DEFAULT_TRIGGER_RUNS_PER_HOUR)), MAX_TRIGGER_RUNS_PER_HOUR);
    return policy.maxRunsPerHour ? Math.min(mine, policy.maxRunsPerHour) : mine;
}
const HOUR_MS = 60 * 60_000;
/**
 * Why a triggered run may not start now, or undefined when it may: the policy's clause first, then the limit per hour
 * (`recent`: when this routine was last started by events, epoch ms).
 */
export function triggerRefusal(input) {
    const { policy, event, now } = input;
    if (policy.events === "off")
        return { code: "not_allowed", message: "The workspace policy does not let routines start on an event: they run on their timetable or when someone asks." };
    if (event.outside && policy.events === "polled")
        return { code: "outside_not_allowed", message: "The workspace policy lets routines start only on what this machine watches, not on events your organisation forwards." };
    const limit = triggerRunsPerHour(policy, input.perHour);
    const lastHour = input.recent.filter((t) => now - t < HOUR_MS).length;
    if (lastHour >= limit)
        return { code: "rate_limited", message: `It already started ${lastHour} ${lastHour === 1 ? "time" : "times"} in the last hour on an event, the most it may (${limit} ${limit === 1 ? "time" : "times"} an hour).` };
    return undefined;
}
//# sourceMappingURL=routineTriggers.js.map